import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Box, ListItem, Typography } from '@mui/material';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { getModifierProductDetails, getProductOptionsDetails } from 'components/menu/model/Menu';
import { IUserReadResourceV12 } from 'components/order/model/Order';
import { addOpacity } from 'lib/helpers';
import { isDefined } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import { UserAvatar } from 'lib/UserAvatar';

const PREFIX = 'ViewBillOrderItem';

const classes = {
    productQuantity: `${PREFIX}productQuantity`,
    productInfo: `${PREFIX}productInfo`,
    productModifiers: `${PREFIX}productModifiers`,
    productPrice: `${PREFIX}productPrice`,
    avatar: `${PREFIX}avatar`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    width: '100%',
    flexShrink: 0,
    [`& .${classes.productQuantity}`]: {
        minWidth: '10%',
        marginRight: theme.spacing(0.5)
    },

    [`& .${classes.productInfo}`]: {
        flex: 1
    },

    [`& .${classes.productModifiers}`]: {
        color: addOpacity(theme.palette.text.primary, 0.6)
    },

    [`& .${classes.productPrice}`]: {
        minWidth: '20%',
        textAlign: 'right'
    },

    [`& .${classes.avatar}`]: {
        width: '32px',
        height: '32px'
    }
}));

interface IProps {
    orderItem: IItemReadResourceV10;
    allItems: IItemReadResourceV10[];
    index?: number;
    onClick?: (item: IItemReadResourceV10) => void;
    showClaimedBy?: boolean;
    user?: IUserReadResourceV12;
    showPricePerOne?: boolean;
}

export const ViewBillOrderItem: React.FC<React.PropsWithChildren<IProps>> = ({
    orderItem,
    showClaimedBy,
    allItems,
    index,
    onClick,
    showPricePerOne,
    user
}) => {
    const getCurrencyString = useCurrencyString();

    const modifierProducts = allItems.filter(
        item => item.parentReferenceId && item.parentReferenceId === orderItem.referenceId
    );
    const [modifierProductsString, calculatedItemPrice] = React.useMemo(() => {
        const [modifierString, modifierPrice] = getModifierProductDetails(
            modifierProducts,
            orderItem.quantity
        );
        return [modifierString, orderItem.cost + modifierPrice];
    }, [orderItem.cost, modifierProducts, orderItem.quantity]);
    const [modifierOptionsString, calculatedOptionsPrice] = React.useMemo(
        () => getProductOptionsDetails(orderItem.modifiers),
        [orderItem.modifiers]
    );

    const totalPrice = React.useMemo(
        () => Math.round((calculatedItemPrice + calculatedOptionsPrice) * orderItem.quantity * 100) / 100,
        [calculatedItemPrice, calculatedOptionsPrice, orderItem.quantity]
    );

    const handleClick = React.useCallback(() => {
        if (isDefined(onClick) && isDefined(index)) {
            onClick(orderItem);
        }
    }, [onClick, index, orderItem]);

    if (orderItem.parentReferenceId) {
        return null;
    }

    return (
        <StyledListItem onClick={handleClick} disableGutters>
            {showClaimedBy ? (
                <Box marginRight={0.5}>
                    <UserAvatar user={user} classNames={classes.avatar} />
                </Box>
            ) : (
                <Typography className={classes.productQuantity}>
                    {!showPricePerOne ? orderItem.quantity : 1}
                    {'x'}
                </Typography>
            )}
            <Box className={classes.productInfo}>
                <Typography>{orderItem.productName}</Typography>
                <Typography variant="caption" className={classes.productModifiers}>
                    {modifierProductsString}
                    {modifierOptionsString}
                </Typography>
            </Box>
            <Typography className={classes.productPrice}>
                {getCurrencyString(showPricePerOne ? totalPrice / orderItem.quantity : totalPrice)}
            </Typography>
        </StyledListItem>
    );
};
